import { LAYOUT_FIELD_TYPES } from "@/lib/fieldTypeCatalog";
import { formatFieldDisplayValue, type NormalizedField } from "../form/runtimeModel";

export type MasterdataDisplayRow = {
  key: string;
  label: string;
  value: string;
};

const HIDDEN_MASTERDATA_KEYS = new Set([
  "hero_address_id",
  "sub_location_id",
  "sub_location_name",
  "external_id",
  "name",
  "bezeichnung",
  "plan_id",
  "plan_page",
  "plan_point",
  "label_text",
  "label_x",
  "label_y",
  "coordinate_mode",
  "page_number",
]);

/** Technische Feldschlüssel ohne Leerzeichen (z. B. ist_die_t_r_eine_fluchtt_r). */
function looksLikeFieldKey(key: string): boolean {
  return /^[a-z0-9]+(?:_[a-z0-9]+)+$/i.test(key) || /^[a-z][a-z0-9_]{2,}$/i.test(key);
}

export function formatMasterdataDisplayValue(value: unknown): string {
  if (value == null || value === "") return "—";
  if (typeof value === "boolean") return value ? "Ja" : "Nein";
  if (Array.isArray(value)) {
    return value.map((v) => formatMasterdataDisplayValue(v)).join(", ") || "—";
  }
  if (typeof value === "object") {
    const r = value as Record<string, unknown>;
    return String(r.label ?? r.name ?? r.external_id ?? JSON.stringify(r));
  }
  return String(value);
}

/**
 * Stammdaten-Zeilen mit konsequenten Fragetiteln aus dem Formularschema.
 * Orphan-Keys ohne Label (alte/umbenannte field_keys) werden nicht als Rohschlüssel gezeigt.
 */
export function buildMasterdataDisplayRows(
  masterdata: Record<string, unknown> | null | undefined,
  schemaFields: NormalizedField[],
): MasterdataDisplayRow[] {
  const data =
    masterdata && typeof masterdata === "object" ? (masterdata as Record<string, unknown>) : {};

  const schemaRows = schemaFields
    .filter((field) => field.effective_scope === "masterdata")
    .filter((field) => !LAYOUT_FIELD_TYPES.has(field.effective_field_type))
    .filter((field) => field.resolved_visible !== false)
    .filter((field) => {
      const key = String(field.field_key || "").trim();
      return key && !HIDDEN_MASTERDATA_KEYS.has(key);
    })
    .map((field) => {
      const key = String(field.field_key).trim();
      const label = String(field.effective_label || "").trim() || key;
      return {
        key,
        label,
        value: formatFieldDisplayValue(field, data[key]),
      };
    });

  // Schema vorhanden: nur Fragetitel aus dem Formular, keine orphan-Keys aus den Rohdaten
  if (schemaRows.length) return schemaRows;

  // Fallback ohne Schema: keine technischen snake_case-Keys als Titel
  return Object.keys(data)
    .filter((key) => !HIDDEN_MASTERDATA_KEYS.has(key))
    .filter((key) => !looksLikeFieldKey(key))
    .map((key) => ({
      key,
      label: key,
      value: formatMasterdataDisplayValue(data[key]),
    }))
    .filter((row) => row.value !== "—");
}
